import { Injectable, NgZone, effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AutoRefreshTokenService, KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { SessionExpiredDialogComponent } from '../shared/session-expired/session-expired-dialog.component';
import { LogoutAction } from '../store/auth/auth.actions';

/**
 * Watches the real Keycloak session lifetime and forces a clean exit when it
 * ends. The SPA and Keycloak are cross-site, so every iframe-based session
 * check is disabled (see main.ts): without this service nothing reacts when
 * the server-side SSO session dies and the app keeps running on a stale token.
 *
 * Three triggers converge on the same exit path (blocking dialog → logout →
 * /error/session-expired):
 *  - a timer armed on `refreshTokenParsed.exp`, the actual server-side session
 *    deadline: when it fires and a forced refresh fails, the session is gone;
 *  - the `AuthRefreshError` / `AuthLogout` adapter events (refresh rejected,
 *    token cleared after a 400 from the token endpoint);
 *  - 401 API responses, routed here by the GlobalErrorHandler.
 *
 * It also (re)configures AutoRefreshTokenService on every token emission,
 * deriving the activity window from `exp - iat` of the refresh token — which
 * IS the realm SSO Session Idle. No session duration is hardcoded client-side:
 * changing the realm settings on Keycloak is enough.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionExpiryService {
  /** Grace added to the deadline so the check runs after the real expiry. */
  private static readonly EXPIRY_GRACE_MS = 2000;

  private readonly keycloak = inject(Keycloak);
  private readonly autoRefreshTokenService = inject(AutoRefreshTokenService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  private expiryTimerId?: number;
  private expired = false;

  constructor() {
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const event = keycloakSignal();

      switch (event.type) {
        case KeycloakEventType.Ready:
        case KeycloakEventType.AuthSuccess:
        case KeycloakEventType.AuthRefreshSuccess:
          this.configureFromTokens();
          break;
        case KeycloakEventType.AuthRefreshError:
        case KeycloakEventType.AuthLogout:
          this.onSessionExpired();
          break;
      }
    });
  }

  /**
   * Entry point for 401 API responses (GlobalErrorHandler). A 401 with a live
   * SSO session only means the access token went stale: the forced refresh
   * recovers silently. When the refresh fails too, the session is over.
   */
  public handleUnauthorized(): void {
    if (!this.keycloak.authenticated) {
      this.router.navigate(['/login']);
      return;
    }

    this.keycloak.updateToken(-1).catch(() => this.onSessionExpired());
  }

  /**
   * Ensures the token is fresh before a sensitive action (e.g. signing a
   * training). Resolves true when a valid token is available; on failure the
   * session-expired exit path starts and the action must not proceed.
   */
  public async ensureFreshToken(): Promise<boolean> {
    if (!this.keycloak.authenticated) {
      this.onSessionExpired();
      return false;
    }

    try {
      await this.keycloak.updateToken(-1);
      return true;
    } catch {
      this.onSessionExpired();
      return false;
    }
  }

  private configureFromTokens(): void {
    const refreshToken = this.keycloak.refreshTokenParsed;

    if (this.expired || !this.keycloak.authenticated || !refreshToken?.exp || !refreshToken?.iat) {
      return;
    }

    // exp - iat of the refresh token is the realm SSO Session Idle (or the
    // client session idle when shorter): the activity window within which the
    // library keeps auto-refreshing follows the realm without local constants.
    this.autoRefreshTokenService.start({
      onInactivityTimeout: 'none',
      sessionTimeout: (refreshToken.exp - refreshToken.iat) * 1000,
    });

    this.scheduleExpiryCheck(refreshToken.exp);
  }

  private scheduleExpiryCheck(refreshExpSeconds: number): void {
    this.clearExpiryTimer();

    // timeSkew corrects the local clock against the server one.
    const skewMs = (this.keycloak.timeSkew ?? 0) * 1000;
    const delay = Math.max(
      refreshExpSeconds * 1000 + skewMs - Date.now() + SessionExpiryService.EXPIRY_GRACE_MS,
      SessionExpiryService.EXPIRY_GRACE_MS,
    );

    this.ngZone.runOutsideAngular(() => {
      this.expiryTimerId = window.setTimeout(() => {
        // A successful forced refresh re-enters configureFromTokens via the
        // AuthRefreshSuccess event and re-arms the timer; a failure means the
        // server-side session is really over.
        this.keycloak.updateToken(-1).catch(() => this.onSessionExpired());
      }, delay);
    });
  }

  private onSessionExpired(): void {
    if (this.expired) {
      return;
    }

    this.expired = true;
    this.clearExpiryTimer();

    this.ngZone.run(() => {
      this.dialog
        .open(SessionExpiredDialogComponent, { width: '420px', disableClose: true })
        .afterClosed()
        .subscribe(() => this.store.dispatch(new LogoutAction('/error/session-expired')));
    });
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimerId !== undefined) {
      window.clearTimeout(this.expiryTimerId);
      this.expiryTimerId = undefined;
    }
  }
}
